/**
 * Which glyph a master gets in the menu.
 *
 * The menu is generated from a sport's own definition, and a master carries no
 * icon — nobody declares one — so this derives it from the two things a master
 * does say: what it is **bound to**, and what it is **called**.
 *
 * The binding comes first. It is the only part that cannot be wrong: a master
 * bound to `venue` is a place whatever it is named, and one bound to
 * `outcome-codes` is a governed list of words. Letting the name lead put a pair
 * of swords on cricket's "Ways of getting out", because the code `bout_result`
 * contains the word "bout".
 *
 * Within a binding, the name chooses. "Team", "Player", "Season", "Ground" are
 * cross-sport words — a swimming meet and a cricket season are both seasons, a
 * jockey and a swimmer are both people — so matching on the word gives an icon
 * that *means* something, which is the only kind worth drawing. Nothing in the
 * vocabulary names a sport, and a seventh sport should need no line here.
 *
 * When the word is unfamiliar the choice is spread by a stable hash of the code,
 * so two masters nobody has a word for still differ. The previous menu gave every
 * master in a group the *same* glyph — swimming showed three identical trophies,
 * and one map pin on both a pool and a list of words. An icon that cannot tell
 * two rows apart is decoration.
 *
 * A hash is a weak reason for a picture, which is why it is last. The real fix is
 * for a master to be able to *say* — an `icon` on `MasterDef`, which the sport
 * editor would then offer — and this is the seam that would read it.
 */

import type { IconName } from "@/components/Icons";
import type { BindingKind } from "@/lib/api/types";

/** Words, and what each one means. First match wins, so specific words lead. */
type Vocabulary = readonly (readonly [readonly string[], IconName])[];

/** People, by what they do rather than by the sport they do it in. */
const PARTICIPANTS: Vocabulary = [
  [["umpire", "referee", "judge", "steward", "commissaire", "official", "marshal"], "official"],
  [["coach", "trainer", "manager", "selector"], "coach"],
  [["owner", "promoter", "syndicate", "sponsor", "agent"], "deal"],
  // Animals run before "team", so a horse is not a squad, and before the
  // people words, so a "horse trainer" master is still a person.
  [["horse", "greyhound", "dog", "animal", "mount"], "hoof"],
  [["jockey", "driver", "pilot", "rider", "swimmer", "boxer", "player", "athlete"], "user"],
  [["person", "individual", "member", "constituent", "contact"], "users"],
  [["country", "nation", "state", "region"], "flag"],
  [["team", "club", "franchise", "squad", "side", "crew", "stable", "organisation"], "club"],
];

/**
 * Containers, outermost first — a tour holds a season holds a stage.
 *
 * Each tier gets its own shape rather than one trophy for all of them. Cricket
 * declares a tour, a series, a league, a season and a stage, and five identical
 * trophies down the Structure group is the same failure as one glyph per binding:
 * it reads as repetition, and the names end up doing all the work anyway.
 *
 * The shapes are chosen to mean the tier, not to be merely different: a tour
 * travels, a series is a set, a league is what a trophy is won in, a season is a
 * span of dates, a stage is a point along the way.
 */
const CONTAINERS: Vocabulary = [
  [["tour", "circuit"], "route"],
  [["series", "rubber"], "layers"],
  // "meet" is a swimming championship — the outermost container. Horse racing's
  // "meeting" is a day at a racecourse and lands in the stage tier below, which
  // is the one place two near-identical words genuinely mean different tiers.
  [["league", "championship", "cup", "division", "competition", "meet"], "trophy"],
  [["festival", "carnival", "games"], "globe"],
  [["season", "year", "edition", "campaign"], "season"],
  [["stage", "round", "group", "phase", "week", "day", "meeting", "session", "leg"], "stage"],
  [["match", "fixture", "game", "bout", "race", "heat", "event", "card"], "swords"],
];

/** A governed list of the sport's own words. Belts are the one that earns its
 *  own glyph: a title is a thing you hold, not a way something ended. */
const CODES: Vocabulary = [
  [["belt", "title", "medal", "award", "honour", "trophy"], "award"],
  [["status", "outcome", "result", "dismissal", "way", "method", "reason", "code"], "list"],
];

/**
 * Per binding: the words that may choose, then the shapes to fall back to.
 *
 * A single fallback means the kind has exactly one shape and the name cannot
 * improve on it. Several means masters of that kind need telling apart.
 */
const BY_BINDING: Record<
  BindingKind,
  { words: Vocabulary; fallback: readonly IconName[] }
> = {
  participant: { words: PARTICIPANTS, fallback: ["users", "user", "club", "network"] },
  "competition-node": {
    words: CONTAINERS,
    fallback: ["layers", "stage", "bracket", "route", "flag"],
  },
  // One shape each: a place is a place, and history is history.
  venue: { words: [], fallback: ["pin"] },
  "outcome-codes": { words: CODES, fallback: ["list"] },
  "name-history": { words: [], fallback: ["archive"] },
  media: { words: [], fallback: ["fileText"] },
  transfer: { words: [], fallback: ["deal"] },
};

const LAST_RESORT: IconName = "circleDot";

/** A small, stable string hash. Not cryptographic — it only has to give the same
 *  answer every time for the same code, so an icon never moves between loads. */
function hash(value: string): number {
  let h = 0;
  for (let i = 0; i < value.length; i += 1) h = (h * 31 + value.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/**
 * The words in a name.
 *
 * Split on anything that is not a letter, so `race_day` yields "day" and
 * "Grantee Reports" yields "report". Plurals are handled by also offering the
 * singular, which is crude and enough: the vocabulary is words, not grammar.
 */
export function wordsOf(...parts: string[]): Set<string> {
  const words = parts
    .join(" ")
    .toLowerCase()
    .split(/[^a-z]+/)
    .filter(Boolean)
    .flatMap((word) =>
      word.endsWith("s") && word.length > 3 ? [word, word.slice(0, -1)] : [word],
    );
  return new Set(words);
}

export function masterIcon(master: {
  code: string;
  label: string;
  bindingKind: BindingKind;
}): IconName {
  const binding = BY_BINDING[master.bindingKind];
  if (!binding) return LAST_RESORT;

  const words = wordsOf(master.label, master.code);
  for (const [vocabulary, icon] of binding.words) {
    if (vocabulary.some((word) => words.has(word))) return icon;
  }

  const { fallback } = binding;
  if (fallback.length === 0) return LAST_RESORT;
  return fallback[hash(master.code) % fallback.length];
}
